import { Injectable } from '@angular/core';
import { _Service } from './_service';
import {
  AppId,
  ChainFacets,
  ChainFilteredPage,
  Company,
  CompanyAdminForm,
  CompanyAdminUpdate,
  CompanySite,
  DDSPreviewData,
  Document,
  HasIdName,
  Page,
  Product,
  ProductChain,
  Report,
  ReportArchive,
  TracesProduct,
} from '../../model';
import { buildQuery } from '../../util';
import { Params } from '@angular/router';
import { HttpOptions } from './http-options.model';

export interface StripeSubscription {
  id: string;
  status: string;
  price: string;
  cancelAt?: Date;
}

@Injectable({ providedIn: 'root' })
export class CompanyService extends _Service<Company> {
  constructor() {
    super('api/company');
  }

  override list = async (queryParams: Params) =>
    this.http.get<Page<Company>>('api/admin/company' + buildQuery(queryParams));
  reportsForCompany = async (id: string, queryParams: Params) =>
    this.http.get<ChainFilteredPage<Report>>(`api/company/${id}/report` + buildQuery(queryParams));
  documentListForCompany = async (companyId: AppId, queryParams: Params) =>
    this.http.get<Page<Document>>(`api/company/${companyId}/document` + buildQuery(queryParams));
  allDocumentsForCompanyTeam = async (companyId: AppId, queryParams: Params, options?: HttpOptions) =>
    this.http.get<Page<Document>>(`api/company/${companyId}/all-documents` + buildQuery(queryParams), options);
  documentListForSite = async (siteId: AppId, queryParams: Params) =>
    this.http.get<Page<Document>>(`api/site/${siteId}/document` + buildQuery(queryParams));
  reportArchiveListForSite = async (siteId: AppId, queryParams: Params) =>
    this.http.get<ChainFilteredPage<ReportArchive>>(`api/site/${siteId}/archive` + buildQuery(queryParams));
  chainListForSite = async (id: string, queryParams: Params) =>
    this.http.get<Page<Product>>(`api/site/${id}/chain` + buildQuery(queryParams));
  chainFacetsForSite = async (id: string, queryParams: Params) =>
    this.http.get<ChainFacets>(`api/site/${id}/chain/facets` + buildQuery(queryParams));
  chainTagSuggestions = async () => this.http.get<string[]>('api/chain/tag-suggestions');
  chainsForSite = async (id: string) => this.http.get<Product>(`api/site/${id}/chain/all`);
  chain = async (productId: string) => this.http.get<Product>(`api/chain/${productId}`);
  chainEntries = async (productId: string) => this.http.get<ProductChain[]>(`api/chain/${productId}/entries`);
  chainsForCompany = async (id: string) => this.http.get<Page<Product>>(`api/company/${id}/chain/all`);
  findCompaniesByName = async (q: string) => this.http.get<HasIdName[]>(`api/company/find?q=${encodeURIComponent(q)}`);
  listAll = async () => await this.http.get<Company[]>('api/company/all');
  addProduct = async (site: CompanySite, product: Product) =>
    this.http.post<Product>(`api/site/${site.id}/chain`, product);
  updateProduct = async (id: AppId, data: { product: Product }) => this.http.put<Product>(`api/chain/${id}`, data);
  updateProductDraft = async (id: AppId, draft: boolean) =>
    this.http.put<Product>(`api/product/${id}/draft`, { draft });
  deleteProduct = async (chain: Product) => this.http.delete<any>(`api/product/${chain.id}`);

  tracesProductListForSite = async (id: string, queryParams: Params) =>
    this.http.get<Page<TracesProduct>>(`api/site/${id}/traces-product` + buildQuery(queryParams));
  tracesProduct = async (productId: string) => this.http.get<TracesProduct>(`api/traces-product/${productId}`);
  addTracesProduct = async (site: CompanySite, product: TracesProduct) =>
    this.http.post<TracesProduct>(`api/site/${site.id}/traces-product`, product);
  updateTracesProduct = async (id: AppId, product: TracesProduct) =>
    this.http.put<TracesProduct>(`api/traces-product/${id}`, product);
  previewTracesProduct = async (id: AppId) =>
    this.http.get<DDSPreviewData>(`api/traces-product/${id}/create-dds-preview`);
  startTracesProduct = async (id: AppId) => this.http.post<TracesProduct>(`api/traces-product/${id}/create-dds`, {});
  retractTracesProduct = async (id: AppId) => this.http.post<void>(`api/traces-product/${id}/retract-dds`, {});
  deleteTracesProduct = async (chain: TracesProduct) => this.http.delete<any>(`api/traces-product/${chain.id}`);

  deleteChain = async (chain: ProductChain) => this.http.delete<any>(`api/chain/${chain.id}`);
  setBuyerProof = async (productId: string, buyerSiteId: string, documentIds: string[]) =>
    this.http.put<string[]>(`api/chain/${productId}/buyer/${buyerSiteId}/proof`, { documents: documentIds });
  createCompany = async (company: Company) => this.http.post<HasIdName & { site: HasIdName }>(`api/company`, company);
  createSite = async (company: Company, site: CompanySite) =>
    this.http.post<HasIdName>(`api/company/${company.id}/site`, site);
  updateSite = async (id: AppId, site: CompanySite) => this.http.put<HasIdName>(`api/site/${id}`, site);
  stripeLink = async (companyId: AppId, returnUrl: string) =>
    this.http.post<{ link: string }>(`api/company/${companyId}/stripe`, { returnUrl });
  stripePay = async (companyId: AppId, plan: string, returnUrl: string, couponCode?: string) =>
    this.http.post<{ link?: string; error?: string }>(`api/company/${companyId}/stripe/create-subscription`, {
      plan,
      returnUrl,
      couponCode,
    });
  stripeSubs = async (companyId: AppId) =>
    this.http.get<StripeSubscription[]>(`api/company/${companyId}/stripe/subscription`);
  // GET/PUT /api/company/{company}/admin — the symmetric admin form pair. Replaces the four
  // legacy admin calls (detail + users + credentials + legal norms + a mismatched PUT) with one
  // GET and one PUT (openspec/changes/admin-company-form-api).
  adminForm = async (companyId: AppId) => this.http.get<CompanyAdminForm>(`api/company/${companyId}/admin`);
  updateCompanyAdmin = async (companyId: AppId, admin: CompanyAdminUpdate) =>
    this.http.put<void>(`api/company/${companyId}/admin`, admin);
}
